import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { RESULT_ROW_HEIGHT, type FilterKey } from '../logic/constants'
import {
  buildNavigableItems,
  getResultTabRowCount,
  getSectionToggleKey,
  getVirtualRange,
  getVisibleSectionRows,
  getWindowedRows,
  type SectionView,
} from '../logic/globalSearchSections'
import type { RenderedSectionRows, Row, Section } from '../logic/types'

interface GlobalSearchNavigationOptions {
  query: string
  activeFilter: FilterKey
  sections: Section[]
  isPreview: boolean
  resultsRef: RefObject<HTMLDivElement>
}

const NO_RENDERED_ROWS: RenderedSectionRows = { rows: [], beforeHeight: 0, afterHeight: 0 }

const toggleKey = (keys: Set<string>, key: string) => {
  const next = new Set(keys)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}

/**
 * Where the user is in the result list: the active item the arrow keys and
 * Enter act on, the All tab's collapsed and expanded sections, and the scroll
 * position a result tab virtualizes by. All of it starts over for a new search
 * or tab.
 */
export function useGlobalSearchNavigation({
  query,
  activeFilter,
  sections,
  isPreview,
  resultsRef,
}: GlobalSearchNavigationOptions) {
  const isAllTab = activeFilter === 'all'
  const [activeIndex, setActiveIndex] = useState(0)
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const [scrollTop, setScrollTop] = useState(0)
  const pendingActiveKeyRef = useRef<string | null>(null)

  const view = useMemo<SectionView>(
    () => ({ isAllTab, expanded, collapsed }),
    [isAllTab, expanded, collapsed]
  )
  const items = useMemo(() => buildNavigableItems(sections, view), [sections, view])
  const indexOf = useMemo(() => new Map(items.map((item, index) => [item.key, index])), [items])
  // A result tab shows one section as a virtualized list.
  const tabSection = !isAllTab && sections.length === 1 ? sections[0] : null
  const tabRowCount = tabSection ? getResultTabRowCount(tabSection, isPreview) : 0
  const activeItemCount = tabSection ? tabRowCount : items.length
  const range = useMemo(
    () => (tabSection && tabRowCount > 0 ? getVirtualRange(tabRowCount, scrollTop) : null),
    [tabSection, tabRowCount, scrollTop]
  )

  useEffect(() => {
    setActiveIndex(0)
    setExpanded(new Set())
    setCollapsed(new Set())
    setScrollTop(0)
    if (resultsRef.current) resultsRef.current.scrollTop = 0
  }, [query, activeFilter, resultsRef])

  useEffect(() => {
    setActiveIndex((index) => Math.min(index, Math.max(activeItemCount - 1, 0)))
  }, [activeItemCount])

  useEffect(() => {
    const pendingActiveKey = pendingActiveKeyRef.current
    if (!pendingActiveKey) return
    const nextIndex = indexOf.get(pendingActiveKey)
    if (nextIndex === undefined) return
    pendingActiveKeyRef.current = null
    setActiveIndex(nextIndex)
  }, [indexOf])

  useEffect(() => {
    if (!isAllTab) return
    resultsRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, isAllTab, resultsRef])

  /** Arrow keys: a result tab scrolls to keep the active row in view. */
  const moveActiveIndex = (direction: -1 | 1) => {
    setActiveIndex((current) => {
      const scroller = resultsRef.current
      if (!tabSection || !scroller) {
        return Math.max(0, Math.min(current + direction, activeItemCount - 1))
      }

      const rowCapacity = Math.max(1, Math.floor((scroller.clientHeight - 12) / RESULT_ROW_HEIGHT))
      const firstVisibleIndex = Math.max(0, Math.ceil(scroller.scrollTop / RESULT_ROW_HEIGHT))
      const lastVisibleIndex = Math.min(activeItemCount - 1, firstVisibleIndex + rowCapacity - 1)

      if (direction < 0) {
        if (current <= firstVisibleIndex) {
          const next = Math.max(0, firstVisibleIndex - 1)
          scroller.scrollTop = next * RESULT_ROW_HEIGHT
          return next
        }
        return Math.max(0, current - 1)
      }

      if (current >= lastVisibleIndex) {
        const next = Math.min(activeItemCount - 1, lastVisibleIndex + 1)
        const nextFirstVisible = Math.max(0, next - rowCapacity + 1)
        scroller.scrollTop = nextFirstVisible * RESULT_ROW_HEIGHT
        return next
      }

      return Math.min(current + 1, activeItemCount - 1)
    })
  }

  return {
    activeIndex,
    setActiveIndex,
    expanded,
    collapsed,
    items,
    indexOf,
    tabSection,
    activeItemCount,
    range,
    setScrollTop,
    moveActiveIndex,
    toggleSectionCollapsed: (key: string) => setCollapsed((current) => toggleKey(current, key)),
    /** "See more" / "See less" on an All-tab section; a collapse can keep its toggle active. */
    toggleExpanded: (key: string, options?: { focusToggleAfterCollapse?: boolean }) => {
      if (options?.focusToggleAfterCollapse) {
        pendingActiveKeyRef.current = getSectionToggleKey(key, 'toggle')
      }
      setExpanded((current) => toggleKey(current, key))
    },
    /**
     * The rows a section renders: on the All tab its visible rows, each with its
     * place in the arrow-key order; on a result tab the scrolled-to window.
     */
    getRenderedRows: (
      section: Section,
      getTabRow: (index: number) => Row | null
    ): RenderedSectionRows => {
      if (!isAllTab) {
        return range ? getWindowedRows(range, tabRowCount, getTabRow) : NO_RENDERED_ROWS
      }
      return {
        rows: getVisibleSectionRows(section, view).map((row) => ({
          row,
          index: indexOf.get(row.key) ?? -1,
        })),
        beforeHeight: 0,
        afterHeight: 0,
      }
    },
  }
}
